import api from './api';
import { normalizeLanguage } from '../constants/Languages';
import { PrepaymentMode, normalizeDepositType, normalizePrepaymentMode } from '../constants/PrepaymentMode';
import { isFreeServicePrice } from '../utils/freeServiceUtils';
import { readVatClassification, toVatPayload } from '../utils/vatClassification';

const normalizeSupportedLanguages = (supportedLanguages = []) => {
  const languages = supportedLanguages
    .map((language) => normalizeLanguage(language))
    .filter((language, index, array) => array.indexOf(language) === index);

  return languages.length > 0 ? languages : ['en'];
};

const normalizeLocalizedDictionary = (value) => {
  if (!value || typeof value !== 'object') {
    return {};
  }

  return Object.entries(value).reduce((accumulator, [key, entryValue]) => {
    if (typeof entryValue !== 'string') {
      return accumulator;
    }

    accumulator[normalizeLanguage(key)] = entryValue;
    return accumulator;
  }, {});
};

export const parseComboDurationToMinutes = (duration) => {
  if (typeof duration === 'number' && Number.isFinite(duration)) {
    return duration;
  }

  if (typeof duration !== 'string' || !duration) {
    return 0;
  }

  const parts = duration.split(':');
  if (parts.length < 2) {
    return 0;
  }

  const hours = Number.parseInt(parts[0], 10) || 0;
  const minutes = Number.parseInt(parts[1], 10) || 0;
  return (hours * 60) + minutes;
};

const formatDurationForBackend = (minutes) => {
  const parsedMinutes = Number.parseInt(minutes, 10);
  const safeMinutes = Number.isFinite(parsedMinutes) && parsedMinutes > 0 ? parsedMinutes : 0;
  const hours = Math.floor(safeMinutes / 60);
  const mins = safeMinutes % 60;
  return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
};

const buildPrepaymentPayload = (combo) => {
  const prepaymentMode = normalizePrepaymentMode(combo.prepaymentMode);
  const depositValue = Number(combo.depositValue);
  const hasDeposit = prepaymentMode !== PrepaymentMode.NotRequired && Number.isFinite(depositValue) && depositValue > 0;

  return {
    prepaymentMode,
    depositType: hasDeposit ? normalizeDepositType(combo.depositType) : null,
    depositValue: hasDeposit ? depositValue : null,
  };
};

export const toFormCombo = (combo) => ({
  id: combo?.id ?? null,
  name: normalizeLocalizedDictionary(combo?.name),
  duration: parseComboDurationToMinutes(combo?.duration),
  price: combo?.price ?? '',
  disabled: Boolean(combo?.disabled),
  serviceIds: Array.isArray(combo?.serviceIds) ? [...combo.serviceIds] : [],
  prepaymentMode: normalizePrepaymentMode(combo?.prepaymentMode ?? combo?.PrepaymentMode),
  depositType: normalizeDepositType(combo?.depositType ?? combo?.DepositType),
  depositValue: combo?.depositValue ?? combo?.DepositValue ?? '',
  allowInstallments: (combo?.allowInstallments ?? combo?.AllowInstallments) === true,
  ...readVatClassification(combo),
});

export const toCatalogCombo = (combo) => ({
  id: combo?.id,
  name: combo?.name,
  duration: combo?.duration,
  price: combo?.price,
  serviceIds: Array.isArray(combo?.serviceIds) ? combo.serviceIds : [],
  isCombo: true,
  visibleToCustomers: true,
  disabled: Boolean(combo?.disabled),
  prepaymentMode: combo?.prepaymentMode,
  depositType: combo?.depositType,
  depositValue: combo?.depositValue,
  allowInstallments: combo?.allowInstallments === true,
  ...readVatClassification(combo),
});

export const mergeCatalogCombos = (services, combos) => [
  ...(services || []).filter((service) => !service.isCombo),
  ...(combos || []),
];

const buildPayload = (combo) => {
  const free = isFreeServicePrice(combo.price);
  const pricedCombo = free
    ? { ...combo, prepaymentMode: PrepaymentMode.NotRequired, depositValue: null, allowInstallments: false }
    : combo;

  return {
    name: Object.entries(combo.name || {}).reduce((accumulator, [language, value]) => {
      accumulator[normalizeLanguage(language)] = value;
      return accumulator;
    }, {}),
    duration: formatDurationForBackend(combo.duration),
    price: Number(combo.price) || 0,
    disabled: Boolean(combo.disabled),
    serviceIds: combo.serviceIds,
    ...buildPrepaymentPayload(pricedCombo),
    allowInstallments: pricedCombo.allowInstallments === true,
    ...toVatPayload(combo),
  };
};

const ServiceComboService = {
  toFormCombo,
  normalizeSupportedLanguages,

  getServiceCombos: async (storeId) => {
    const response = await api.get('/service-combos', { _storeId: storeId });
    return Array.isArray(response.data) ? response.data.map(toFormCombo) : [];
  },

  getServiceComboCatalog: async (storeId) => {
    const response = await api.get('/service-combos/catalog', { _storeId: storeId });
    return Array.isArray(response.data) ? response.data.map(toCatalogCombo) : [];
  },

  createServiceCombo: async (storeId, combo) => {
    const response = await api.post('/service-combos', buildPayload(combo), { _storeId: storeId });
    return toFormCombo(response.data);
  },

  updateServiceCombo: async (storeId, comboServiceId, combo) => {
    const response = await api.put(`/service-combos/${comboServiceId}`, buildPayload(combo), { _storeId: storeId });
    return toFormCombo(response.data);
  },

  deleteServiceCombo: async (storeId, comboServiceId) => {
    await api.delete(`/service-combos/${comboServiceId}`, { _storeId: storeId });
  },
};

export default ServiceComboService;
